import { Typography, Stack } from "@mui/material";
import { useMemo } from "react";
import { IngredientFormAssociatedCompany, IngredientFormCompany } from "../../../IngredientForm.types";
import { CompanyAutocomplete } from "src/components/Selector/CompanySelector/CompanyAutocomplete";
import type { CompanyOption } from "src/components/Selector/CompanySelector/CompanyAutocomplete";
import { useTranslation } from "react-i18next";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { FormHelperBox } from "src/components/EntityForm/FormHelperBox";
import { Banner } from "@foodpilot/foods";
import { useIsProductUxV2 } from "src/hooks/useIsProductUxV2";

type AssociatedStructuresFieldProps = {
    value: IngredientFormAssociatedCompany[];
    onChange: (value: IngredientFormAssociatedCompany[]) => void;
    ownedBy: IngredientFormCompany | null;
};

export const AssociatedStructuresField = ({ value, onChange, ownedBy }: AssociatedStructuresFieldProps) => {
    const { t } = useTranslation();
    const { selectedCompany } = useFoodpilotContext();
    const isProductUxV2 = useIsProductUxV2();

    // Convertir les IngredientFormAssociatedCompany en CompanyOption
    const companyOptions: CompanyOption[] = useMemo(() => {
        return value.map((assoc) => assoc.organization as CompanyOption);
    }, [value]);

    // Exclure ownedBy des options
    const excludeIds = useMemo(() => {
        return ownedBy ? [ownedBy.id] : [];
    }, [ownedBy]);

    const handleChange = (companies: CompanyOption[]) => {
        const associations: IngredientFormAssociatedCompany[] = companies.map((company) => ({
            organization: company as IngredientFormCompany,
        }));
        onChange(associations);
    };

    return (
        <Stack flexDirection="column" gap={2}>
            <Typography variant="body-semibold">
                {t("entity.organization.associated_entities.label", "Associated entities:")}
            </Typography>

            <CompanyAutocomplete
                value={companyOptions}
                onChange={handleChange}
                placeholder={t("entity.organization.associated_entities.placeholder", "Add an option")}
                excludeIds={excludeIds}
                hierarchyRootId={selectedCompany?.id}
            />

            {isProductUxV2 ?
                <FormHelperBox>
                    {t(
                        "entity.organization.associated_entities.helper",
                        "Select the companies that use this ingredient. They will be able to complete the data related to their suppliers and, depending on their access rights, manage the associated suppliers.",
                    )}
                </FormHelperBox>
            :   <Banner variant="themed">
                    <Typography variant="body-regular">
                        {t(
                            "entity.organization.associated_entities.helper",
                            "Select the companies that use this ingredient. They will be able to complete the data related to their suppliers and, depending on their access rights, manage the associated suppliers.",
                        )}
                    </Typography>
                </Banner>
            }
        </Stack>
    );
};
